import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InputMask from 'react-input-mask';

import { UserContext } from '../../UserContext';
import { useHttpClient } from '../hooks/useHttpClient';
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';
import { useNotificacoes } from '../hooks/useNotificacoes';

import styles from './CadastroEmpresa.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import Formulario from '../../components/ui/Formulario';
import BotaoSalvar from "../BotaoSalvar";
import {useHasRole} from "../hooks/useHasRole";

function CadastroEmpresa() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const { get, post, put } = useHttpClient();
    const { exibirErro, exibirSucesso } = useNotificacoes();
    const podeAlterar = useHasRole(['ADMINISTRADOR']);


    // safeSubmit já encapsula salvarEmpresa e previne duplo clique
    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarEmpresa);

    const [logoPreview, setLogoPreview] = useState('');
    const [formData, setFormData] = useState({
        nome: '',
        cnpj: '',
        telefone: '',
        email: '',
        site: '',
        missao: '',
        endereco: '',
        complementoEndereco: '',
        bairro: '',
        municipio: '',
        uf: '',
        cep: '',
        logoEmpresa: ''
    });

    const MISSAO_PADRAO =
        'Facilitar o acesso à energia solar ao aproximar geradores e consumidores por meio de uma plataforma intuitiva que estimula eficiência, inovação e responsabilidade ambiental.';

    // 1. Protege rota e carrega empresa se tiver id
    useEffect(() => {
        if (user === null) {
            navigate('/login');
        } else if (id) {
            carregarEmpresa();
        } else {
            // é cadastro novo: preenche missão padrão
            setFormData(f => ({ ...f, missao: MISSAO_PADRAO }));
        }
    }, [user, id, navigate]);

    // 2. Busca dados para edição
    async function carregarEmpresa() {
        try {
            const data = await get(`/hello-sol/empresas/${id}`);
            setFormData(f => ({
                ...f,
                ...data,
                // se o backend não trouxe missão, usa o padrão
                missao: data.missao?.trim() ? data.missao : MISSAO_PADRAO
            }));
            setLogoPreview(data.logoEmpresa);
        } catch (err) {
            exibirErro(err.message || 'Erro ao carregar dados da empresa.');
        }
    }

    // 3. Manipula inputs texto e máscara
    function handleChange(e) {
        const { name, value } = e.target;
        setFormData(f => ({ ...f, [name]: value }));
    }

    function handleLogoChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData(f => ({ ...f, logoEmpresa: reader.result }));
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    }

    // 4. Quando o usuário completa o CEP, já busca endereço
    function handleCepChange(e) {
        const cep = e.target.value;
        setFormData(f => ({ ...f, cep }));
        if (cep.replace(/\D/g, '').length === 8) {
            fetchAddress(cep.replace(/\D/g, ''));
        }
    }

    async function fetchAddress(cepNum) {
        try {
            const resp = await fetch(`https://viacep.com.br/ws/${cepNum}/json/`);
            const data = await resp.json();
            if (data.erro) throw new Error('CEP não encontrado');
            setFormData(f => ({
                ...f,
                endereco: data.logradouro,
                bairro: data.bairro,
                municipio: data.localidade,
                uf: data.uf
            }));
        } catch (err) {
            exibirErro('Erro ao buscar endereço: ' + err.message);
        }
    }

    // 5. Salva ou atualiza via POST/PUT
    async function salvarEmpresa(e) {
        e.preventDefault();
        try {
            if (id) {
                await put(`/hello-sol/empresas/${id}`, formData);
            } else {
                await post(`/hello-sol/empresas`, formData);
            }
            navigate('/home/listar-empresa');
        } catch (err) {
            exibirErro(err.message || 'Erro ao salvar empresa.');
        }
    }

    return (
        <div className={styles_global.container}>
            <h2>
                {id ? 'Alterar Empresa' : 'Cadastrar Empresa'}
            </h2>

            <Formulario onSubmit={safeSubmit}>
                {/* Linha 1 */}
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="cnpj">CNPJ</label>
                        <InputMask
                            id="cnpj"
                            mask="99.999.999/9999-99"
                            name="cnpj"
                            value={formData.cnpj}
                            onChange={handleChange}
                            placeholder="Digite o CNPJ"
                            required>
                            {props => <input {...props}  />}
                        </InputMask>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="nome">Nome</label>
                        <input
                            id="nome"
                            className={styles.campoNomeEmpresa}
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleChange}
                            placeholder="Digite o Nome"
                            required
                        />
                    </div>
                </div>

                {/* Linha 2 */}
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="telefone">Telefone</label>
                        <InputMask
                            id="telefone"
                            mask="(99) 99999-9999"
                            name="telefone"
                            value={formData.telefone}
                            onChange={handleChange}
                            placeholder="Digite o Telefone"
                            required
                        >
                            {props => <input {...props} />}
                        </InputMask>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="email">Email</label>
                        <input
                            id="email"
                            className={styles.campoEmailEmpresa}
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Digite o Email"
                            required
                        />
                    </div>
                </div>

                {/* Linha Missao */}
                <div className={styles.linhaSiteMissao}>
                    <div className={styles_content.formField}>
                        <label htmlFor="site">Site da Empresa</label>
                        <input
                            id="site"
                            name="site"
                            value={formData.site}
                            onChange={handleChange}
                            placeholder="Digite o Site da Empresa"
                            className={styles.campoSite}
                            maxLength={50}
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="missao">Missão da Empresa</label>
                        <textarea
                            id="missao"
                            name="missao"
                            value={formData.missao}
                            onChange={handleChange}
                            placeholder="Descreva a missão da empresa, essa informação aparecerá na fatura."
                            maxLength={300}
                            className={styles.textArea}
                        />
                    </div>
                </div>

                {/* Linha 3 */}
                <div className={styles.linha3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="cep">CEP</label>
                        <InputMask
                            id="cep"
                            className={styles.campoCepEmpresa}
                            mask="99999-999"
                            name="cep"
                            value={formData.cep}
                            onChange={handleCepChange}
                            placeholder="Digite o CEP"
                            required
                        >
                            {props => <input {...props} />}
                        </InputMask>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="endereco">Endereço</label>
                        <input
                            id="endereco"
                            className={styles.campoEnderecoEmpresa}
                            type="text"
                            name="endereco"
                            value={formData.endereco}
                            onChange={handleChange}
                            placeholder="Digite o Endereço"
                            required
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="complementoEndereco">Complemento</label>
                        <input
                            id="complementoEndereco"
                            type="text"
                            name="complementoEndereco"
                            value={formData.complementoEndereco}
                            onChange={handleChange}
                            placeholder="Digite o Complemento"
                        />
                    </div>
                </div>

                {/* Linha 4 */}
                <div className={styles.linha3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="bairro">Bairro</label>
                        <input
                            id="bairro"
                            type="text"
                            name="bairro"
                            value={formData.bairro}
                            onChange={handleChange}
                            placeholder="Digite o Bairro"
                            required
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="municipio">Município</label>
                        <input
                            id="municipio"
                            className={styles.campoMunicipioEmpresa}
                            type="text"
                            name="municipio"
                            value={formData.municipio}
                            onChange={handleChange}
                            placeholder="Digite o Município"
                            required
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="uf">UF</label>
                        <input
                            id="uf"
                            type="text"
                            name="uf"
                            value={formData.uf}
                            onChange={handleChange}
                            placeholder="Digite a UF"
                            maxLength={2}
                            required
                        />
                    </div>
                </div>

                {/* Linha 5 - Logo Empresa */}
                <div className={styles.linhaLogo}>
                    <div className={styles.logoField}>
                        <label htmlFor="logoEmpresa">Logo da Empresa</label>
                        <input
                            id="logoEmpresa"
                            type="file"
                            name="logoEmpresa"
                            accept="image/*"
                            onChange={handleLogoChange}
                        />
                    </div>
                </div>

                {/* Prévia da Logo */}
                {logoPreview && (
                    <div className={styles.logoPreview}>
                        <img src={logoPreview} alt="Prévia da Logo"/>
                    </div>
                )}

                {/* Botão Salvar */}
                <div className={styles_content.buttonGroup}>
                    <BotaoSalvar loading={isSubmitting} onClick={safeSubmit}/>
                </div>
            </Formulario>
        </div>
    );
}

export default CadastroEmpresa;
